import { QuotesIcon } from "@phosphor-icons/react";

import ChatLinkableText from "@/components/ui/chat/ChatLinkableText";
import { cn } from "@/lib/utils";
import { tableCellToString } from "@/lib/utils/aiTableUtils";

export interface BlockquoteProps {
  /** Excerpt or key statement text. */
  content: string;
  /** Speaker or name when known. */
  attribution?: string | null;
  /** Publication or outlet when known. */
  source?: string | null;
  /** Optional class name for the root. */
  className?: string;
}

/**
 * Blockquote for chat: left border, muted surface, decorative quote icon, italic xs body,
 * optional footer: attribution then source (each line when present).
 */
const Blockquote = ({
  content,
  attribution,
  source,
  className,
}: BlockquoteProps) => {
  const attributionText =
    attribution != null ? tableCellToString(attribution) : "";
  const sourceText = source != null ? tableCellToString(source) : "";
  const hasAttribution = attributionText.trim().length > 0;
  const hasSource = sourceText.trim().length > 0;
  const hasFooter = hasAttribution || hasSource;

  return (
    <blockquote
      className={cn(
        "relative rounded-r-lg border-l-4 border-primary/30 bg-muted/30 py-3 pl-4 pr-10",
        className,
      )}
    >
      <QuotesIcon
        className="pointer-events-none absolute top-2.5 right-2.5 size-3 shrink-0 text-muted-foreground/30"
        aria-hidden
      />
      <p className="text-[13px] leading-relaxed italic text-foreground">
        <ChatLinkableText value={content} />
      </p>
      {hasFooter ? (
        <footer className="mt-2 flex flex-col gap-1 text-[11.5px] leading-snug text-muted-foreground">
          {hasAttribution ? (
            <span className="font-medium">
              <ChatLinkableText value={attribution} />
            </span>
          ) : null}
          {hasSource ? (
            <cite className="not-italic">
              <ChatLinkableText value={source} />
            </cite>
          ) : null}
        </footer>
      ) : null}
    </blockquote>
  );
};

export default Blockquote;
